import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { authApi } from "@/services/api";
import { useAuthStore } from "@/store/auth.store";
import {
  WelcomeStep,
  FeaturesStep,
  WorkflowStep,
  FirstEnvelopeStep,
  CompleteStep,
} from "./steps";

// Storage key for persisting step
const ONBOARDING_STEP_KEY = "onboarding_current_step";

// Step definitions
const ONBOARDING_STEPS = [
  { id: "welcome", title: "Welcome" },
  { id: "features", title: "Features" },
  { id: "workflow", title: "How It Works" },
  { id: "first-envelope", title: "Create Your First" },
  { id: "complete", title: "Ready!" },
];

/**
 * Get persisted step from sessionStorage
 */
const getPersistedStep = () => {
  try {
    const stored = sessionStorage.getItem(ONBOARDING_STEP_KEY);
    if (stored) {
      const step = parseInt(stored, 10);
      if (!isNaN(step) && step >= 0 && step < ONBOARDING_STEPS.length) {
        return step;
      }
    }
  } catch {
    // Ignore storage errors
  }
  return 0;
};

/**
 * Persist step to sessionStorage
 */
const persistStep = (step) => {
  try {
    sessionStorage.setItem(ONBOARDING_STEP_KEY, step.toString());
  } catch {
    // Ignore storage errors
  }
};

/**
 * Clear persisted step
 */
const clearPersistedStep = () => {
  try {
    sessionStorage.removeItem(ONBOARDING_STEP_KEY);
  } catch {
    // Ignore storage errors
  }
};

export const Onboarding = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const updateUser = useAuthStore((state) => state.updateUser);
  const [currentStep, setCurrentStep] = useState(getPersistedStep);
  const [isCompleting, setIsCompleting] = useState(false);

  // Persist step changes
  useEffect(() => {
    persistStep(currentStep);
  }, [currentStep]);

  const progress = ((currentStep + 1) / ONBOARDING_STEPS.length) * 100;

  const handleNext = () => {
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  /**
   * Navigate to an allowed route without completing onboarding
   * User can return to /onboarding to continue the wizard
   */
  const handleNavigateAway = (destination) => {
    navigate(destination);
  };

  /**
   * Complete onboarding and navigate to destination
   * This marks onboarding as done and clears persisted step
   */
  const handleComplete = async (destination = "/dashboard") => {
    setIsCompleting(true);
    try {
      // Mark onboarding as complete in the backend
      await authApi.completeOnboarding();

      // Update local user state
      updateUser({ hasCompletedOnboarding: true });

      // Clear persisted step
      clearPersistedStep();

      // Navigate to destination
      navigate(destination);
    } catch (error) {
      console.error("Failed to complete onboarding:", error);
      // Still navigate even if API fails
      updateUser({ hasCompletedOnboarding: true });
      clearPersistedStep();
      navigate(destination);
    }
  };

  const handleSkip = async () => {
    await handleComplete("/dashboard");
  };

  const renderStep = () => {
    switch (ONBOARDING_STEPS[currentStep].id) {
      case "welcome":
        return <WelcomeStep user={user} onNext={handleNext} />;
      case "features":
        return <FeaturesStep onNext={handleNext} onBack={handleBack} />;
      case "workflow":
        return <WorkflowStep onNext={handleNext} onBack={handleBack} />;
      case "first-envelope":
        return (
          <FirstEnvelopeStep
            onNext={handleNext}
            onBack={handleBack}
            onNavigateAway={handleNavigateAway}
          />
        );
      case "complete":
        return <CompleteStep onFinish={() => handleComplete("/dashboard")} />;
      default:
        return null;
    }
  };

  const renderOnboardingStepClass = (index) => {
    if (index === currentStep) {
      return "bg-primary";
    }
    return index < currentStep ? "bg-primary/50" : "bg-muted";
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <Logo size="md" showText text={t("common.appName")} />
            </div>

            {/* Progress indicator */}
            <div className="hidden sm:flex items-center gap-2">
              {ONBOARDING_STEPS.map((step, index) => (
                <div
                  key={step.id}
                  className={cn(
                    "w-2 h-2 rounded-full transition-colors",
                    renderOnboardingStepClass(index),
                  )}
                />
              ))}
            </div>

            {/* Skip button */}
            {currentStep < ONBOARDING_STEPS.length - 1 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSkip}
                disabled={isCompleting}
              >
                {t("common.skip", "Skip")}
              </Button>
            )}
          </div>
        </div>

        {/* Mobile progress bar */}
        <Progress value={progress} className="h-1 rounded-none sm:hidden" />
      </header>

      {/* Main content */}
      <main className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <AnimatePresence mode="wait">
            <motion.div key={currentStep}>{renderStep()}</motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Footer with step indicator */}
      <footer className="fixed bottom-0 left-0 right-0 bg-background/80 backdrop-blur-sm border-t sm:hidden">
        <div className="px-4 py-3">
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <span>
              {t("onboarding.step", "Step")} {currentStep + 1}{" "}
              {t("common.of", "of")} {ONBOARDING_STEPS.length}
            </span>
            <span>•</span>
            <span>{ONBOARDING_STEPS[currentStep].title}</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Onboarding;
